import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

console.log('API Client initialized with URL:', API_URL);

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    console.log('Making request to:', config.baseURL + config.url);
    const authData = localStorage.getItem('authData');
    if (authData) {
      const { token } = JSON.parse(authData);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Single-flight refresh: concurrent 401s share one refresh request
let refreshPromise = null;

const clearSessionAndRedirect = (reason) => {
  localStorage.removeItem('authData');
  localStorage.setItem('loginReason', reason || 'Your session has expired. Please log in again.');
  window.location.href = '/login';
};

const refreshAccessToken = () => {
  if (!refreshPromise) {
    const authData = localStorage.getItem('authData');
    const refreshToken = authData ? JSON.parse(authData).refreshToken : null;

    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token available'));
    }

    // Plain axios call bypasses apiClient interceptors to avoid recursion
    refreshPromise = axios
      .post(`${API_URL}/auth/refresh-token`, { refreshToken })
      .then((response) => {
        const { accessToken, refreshToken: newRefreshToken, user } = response.data.data;
        const stored = localStorage.getItem('authData');
        const storedData = stored ? JSON.parse(stored) : {};
        localStorage.setItem('authData', JSON.stringify({ ...storedData, user: user || storedData.user, token: accessToken, refreshToken: newRefreshToken }));
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error.config;

    if (error.response?.status !== 401 || originalConfig?.skipAuthRedirect) {
      return Promise.reject(error);
    }

    // Already retried once — session is genuinely invalid
    if (originalConfig.authRetry) {
      clearSessionAndRedirect(error.response?.data?.error?.message);
      return Promise.reject(error);
    }

    try {
      const newToken = await refreshAccessToken();
      originalConfig.authRetry = true;
      originalConfig.headers = originalConfig.headers || {};
      originalConfig.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(originalConfig);
    } catch (refreshError) {
      const reason = refreshError.response?.data?.error?.message || error.response?.data?.error?.message;
      clearSessionAndRedirect(reason);
      return Promise.reject(error);
    }
  }
);

// Authentication API
export const authAPI = {
  register: (userData) => apiClient.post('/auth/register', userData),
  login: (email, password, otpChannel = 'email') => apiClient.post('/auth/login', { email, password, otpChannel }),
  adminLogin: (email, password, otpChannel = 'email') => apiClient.post('/auth/admin/login', { email, password, otpChannel }),
  verifyOTP: (userId, otp, purpose = 'login') => apiClient.post('/auth/verify-otp', { userId, code: otp, purpose }),
  resendOTP: (userId, purpose = 'login', otpChannel = 'email') => apiClient.post('/auth/resend-otp', { userId, purpose, otpChannel }),
  logout: () => apiClient.post('/auth/logout'),
  getCurrentUser: () => apiClient.get('/auth/me'),
  verifyPassword: (data) => apiClient.post('/auth/verify-password', data),
};

// Users API
export const usersAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    // Only add filters if they have actual values (not empty strings)
    if (filters.userType && filters.userType !== '') params.append('userType', filters.userType);
    if (filters.status && filters.status !== '') params.append('status', filters.status);
    if (filters.isActive !== undefined && filters.isActive !== '') params.append('isActive', filters.isActive);
    if (filters.search && filters.search !== '') params.append('search', filters.search);
    return apiClient.get(`/users?${params.toString()}`);
  },
  createUser: (userData) => apiClient.post('/users/create', userData),
  getById: (id) => apiClient.get(`/users/${id}`),
  getUserDetails: (id) => apiClient.get(`/users/${id}/details`),
  create: (userData) => apiClient.post('/users', userData),
  update: (id, userData) => apiClient.put(`/users/${id}`, userData),
  delete: (id) => apiClient.delete(`/users/${id}`),
  activate: (id) => apiClient.patch(`/users/${id}/activate`),
  deactivate: (id) => apiClient.patch(`/users/${id}/deactivate`),
  suspend: (id, reason) => apiClient.patch(`/users/${id}/suspend`, { reason }),
  toggleAdminVerification: (id, verified) => apiClient.patch(`/users/${id}/admin-verify`, { verified }),
  getStatistics: () => apiClient.get('/users/statistics'),
};

// Providers API
export const providersAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    return apiClient.get(`/providers?${params.toString()}`);
  },
  getById: (id) => apiClient.get(`/providers/${id}`),
  approve: (id) => apiClient.patch(`/providers/${id}/approve`),
  reject: (id, reason) => apiClient.patch(`/providers/${id}/reject`, { reason }),
  suspend: (id, reason) => apiClient.patch(`/providers/${id}/suspend`, { reason }),
  activate: (id) => apiClient.patch(`/providers/${id}/activate`),
};

// Bookings API
export const bookingsAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    return apiClient.get(`/bookings?${params.toString()}`);
  },
  getById: (id) => apiClient.get(`/bookings/${id}`),
  updateStatus: (id, status) => apiClient.patch(`/bookings/${id}/status`, { status }),
};

// Family Groups API
export const familyAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined && filters.isActive !== '') params.append('isActive', filters.isActive);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    return apiClient.get(`/families/all?${params.toString()}`);
  },
  getMy: () => apiClient.get('/families/my'),
  getById: (id) => apiClient.get(`/families/${id}`),
  create: (familyData) => apiClient.post('/families', familyData),
  update: (id, familyData) => apiClient.put(`/families/${id}`, familyData),
  delete: (id) => apiClient.delete(`/families/${id}`),
  addMember: (id, memberData) => apiClient.post(`/families/${id}/members`, memberData),
  updateMember: (id, memberId, permissions) => apiClient.put(`/families/${id}/members/${memberId}`, permissions),
  removeMember: (id, memberId) => apiClient.delete(`/families/${id}/members/${memberId}`),
};

// Provider Organizations API
export const providerOrgAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined && filters.isActive !== '') params.append('isActive', filters.isActive);
    if (filters.isVerified !== undefined && filters.isVerified !== '') params.append('isVerified', filters.isVerified);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    return apiClient.get(`/provider-organizations/all?${params.toString()}`);
  },
  getMy: () => apiClient.get('/provider-organizations/my'),
  getById: (id) => apiClient.get(`/provider-organizations/${id}`),
  create: (orgData) => apiClient.post('/provider-organizations', orgData),
  update: (id, orgData) => apiClient.put(`/provider-organizations/${id}`, orgData),
  delete: (id) => apiClient.delete(`/provider-organizations/${id}`),
  verify: (id) => apiClient.patch(`/provider-organizations/${id}/verify`),
  addSubuser: (id, subuserData) => apiClient.post(`/provider-organizations/${id}/subusers`, subuserData),
  updateSubuser: (id, subuserId, permissions) => apiClient.put(`/provider-organizations/${id}/subusers/${subuserId}`, permissions),
  removeSubuser: (id, subuserId) => apiClient.delete(`/provider-organizations/${id}/subusers/${subuserId}`),
  reviewProfile: (providerId, data) => apiClient.put(`/provider-profiles/${providerId}/review`, data),
  verifyDocument: (documentId, data) => apiClient.put(`/provider-profiles/documents/${documentId}/verify`, data),
};

// Corporate Organizations API
export const corporateOrgAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.organizationType) params.append('organizationType', filters.organizationType);
    if (filters.isActive !== undefined && filters.isActive !== '') params.append('isActive', filters.isActive);
    if (filters.isVerified !== undefined && filters.isVerified !== '') params.append('isVerified', filters.isVerified);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    return apiClient.get(`/corporate-organizations?${params.toString()}`);
  },
  getById: (id, include = false) => {
    const params = include ? '?include=details' : '';
    return apiClient.get(`/corporate-organizations/${id}${params}`);
  },
  getByCode: (code) => apiClient.get(`/corporate-organizations/code/${code}`),
  create: (orgData) => apiClient.post('/corporate-organizations', orgData),
  update: (id, orgData) => apiClient.put(`/corporate-organizations/${id}`, orgData),
  delete: (id) => apiClient.delete(`/corporate-organizations/${id}`),
  verify: (id) => apiClient.post(`/corporate-organizations/${id}/verify`),
  suspend: (id, data) => apiClient.post(`/corporate-organizations/${id}/suspend`, data),
  reactivate: (id) => apiClient.post(`/corporate-organizations/${id}/reactivate`),
  getStats: (id) => apiClient.get(`/corporate-organizations/${id}/stats`),
  // Admin Management
  addAdmin: (id, adminData) => apiClient.post(`/corporate-organizations/${id}/admins`, adminData),
  listAdmins: (id, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.role) params.append('role', filters.role);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    return apiClient.get(`/corporate-organizations/${id}/admins?${params.toString()}`);
  },
  updateAdminPermissions: (id, adminId, permissions) => apiClient.put(`/corporate-organizations/${id}/admins/${adminId}`, permissions),
  removeAdmin: (id, adminId) => apiClient.delete(`/corporate-organizations/${id}/admins/${adminId}`),
  resendAdminLogin: (id, adminId) => apiClient.post(`/corporate-organizations/${id}/admins/${adminId}/resend-login`),
  // Member Management
  addMember: (id, memberData) => apiClient.post(`/corporate-organizations/${id}/members`, memberData),
  bulkImportMembers: (id, csvContent) => apiClient.post(`/corporate-organizations/${id}/members/bulk`, { csvContent }),
  listMembers: (id, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.membershipStatus) params.append('membershipStatus', filters.membershipStatus);
    if (filters.approvalStatus) params.append('approvalStatus', filters.approvalStatus);
    if (filters.departmentId) params.append('departmentId', filters.departmentId);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    return apiClient.get(`/corporate-organizations/${id}/members?${params.toString()}`);
  },
  getMember: (id, memberId) => apiClient.get(`/corporate-organizations/${id}/members/${memberId}`),
  updateMember: (id, memberId, memberData) => apiClient.put(`/corporate-organizations/${id}/members/${memberId}`, memberData),
  approveMember: (id, memberId) => apiClient.post(`/corporate-organizations/${id}/members/${memberId}/approve`),
  rejectMember: (id, memberId, reason) => apiClient.post(`/corporate-organizations/${id}/members/${memberId}/reject`, { reason }),
  removeMember: (id, memberId, reason) => apiClient.delete(`/corporate-organizations/${id}/members/${memberId}`, { data: { reason } }),
  suspendMember: (id, memberId, reason) => apiClient.post(`/corporate-organizations/${id}/members/${memberId}/suspend`, { reason }),
  reactivateMember: (id, memberId) => apiClient.post(`/corporate-organizations/${id}/members/${memberId}/reactivate`),
  getMemberStats: (id) => apiClient.get(`/corporate-organizations/${id}/members/stats`),
  // Department Management
  createDepartment: (id, deptData) => apiClient.post(`/corporate-organizations/${id}/departments`, deptData),
  listDepartments: (id, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters.parentId) params.append('parentId', filters.parentId);
    return apiClient.get(`/corporate-organizations/${id}/departments?${params.toString()}`);
  },
  getDepartmentHierarchy: (id) => apiClient.get(`/corporate-organizations/${id}/departments/hierarchy`),
  updateDepartment: (id, deptId, deptData) => apiClient.put(`/corporate-organizations/${id}/departments/${deptId}`, deptData),
  deleteDepartment: (id, deptId) => apiClient.delete(`/corporate-organizations/${id}/departments/${deptId}`),
  getDepartmentStats: (id, deptId) => apiClient.get(`/corporate-organizations/${id}/departments/${deptId}/stats`),
};

export default apiClient;
